import {
  ButtonProps,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { forwardRef, useEffect } from 'react';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useSupportBoardHooks } from 'src/services/SupportBoard/useSupportBoardHooks';
import { useSupportBoardState } from 'src/services/SupportBoard/useSupportBoardState';
import { StyledBadge } from '../shared/styles';

const docs = {
  tutorial:
    'https://app.copera.ai/public-doc/af9d3a4e-d14b-4ed1-b5fa-6f63bdd6a5cd',
  faq: 'https://app.copera.ai/public-doc/24e606a4-5eb2-4618-9558-c94d1808cf79',
};

export const Support = forwardRef<any, ButtonProps>(({ ...rest }, ref) => {
  const { translate } = useLocales();

  const { counterSpan, supportLoaded } = useSupportBoardState();
  const { loadSupport, openSupport } = useSupportBoardHooks();

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'supportPopover',
  });

  const handleOpenDoc = (doc: 'tutorial' | 'faq') => {
    window.open(docs[doc], '_blank');
    popupState.close();
  };

  const handleOpenSupport = () => {
    openSupport();
    popupState.close();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (supportLoaded) return;
    loadSupport();
  }, [supportLoaded]);

  return (
    <Box className="support">
      <StyledSupportBadge
        overlap="circular"
        badgeContent={counterSpan?.innerHTML}
        color="error"
        invisible={!counterSpan?.innerHTML || counterSpan?.innerHTML === '0'}
      >
        <Tooltip title="Support" placement="left" arrow>
          <IconButton {...bindTrigger(popupState)} ref={ref} {...rest}>
            <Iconify icon="tabler:help" />
          </IconButton>
        </Tooltip>
      </StyledSupportBadge>

      <SupportPopover
        {...bindMenu(popupState)}
        onClose={() => popupState.close()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          pt={2}
        >
          <Typography variant="h4">Help Center</Typography>
          <IconButton onClick={() => popupState.close()}>
            <Iconify icon="tabler:x" />
          </IconButton>
        </Stack>
        <List className="sublist" sx={{ p: 2 }}>
          <MenuItem onClick={() => handleOpenDoc('tutorial')}>
            <ListItemIcon>
              <Iconify icon="tabler:book-2" />
            </ListItemIcon>
            <ListItemText
              primary={translate('support.title.tutorials')}
              secondary={translate('support.subtitle.tutorials')}
            />
          </MenuItem>
          <MenuItem onClick={() => handleOpenDoc('faq')}>
            <ListItemIcon>
              <Iconify icon="tabler:help" />
            </ListItemIcon>
            <ListItemText
              primary={translate('support.title.faq')}
              secondary={translate('support.subtitle.faq')}
            />
          </MenuItem>
          <MenuItem
            onClick={() => handleOpenSupport()}
            disabled={!supportLoaded}
          >
            <ListItemIcon>
              <Iconify icon="tabler:message-dots" />
            </ListItemIcon>
            <ListItemText
              primary={translate('support.title.support')}
              secondary={translate('support.subtitle.support')}
            />
          </MenuItem>
        </List>
      </SupportPopover>
    </Box>
  );
});

const SupportPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: 360,
    '& .MuiMenuItem-root': {
      alignItems: 'flex-start',
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.support,
      '&:hover': {
        background: theme.palette.action.hover,
      },
      '& .MuiListItemIcon-root': {
        minWidth: theme.spacing(4),
        color: theme.palette.primary.main,
        marginTop: theme.spacing(0.25),
        svg: {
          width: theme.spacing(3),
          height: theme.spacing(3),
        },
      },
      '& .MuiListItemText-primary': {
        ...theme.typography.h6,
      },
      '& .MuiListItemText-secondary': {
        whiteSpace: 'break-spaces',
      },
    },
    '& .MuiMenuItem-root:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
  },
}));

const StyledSupportBadge = styled(StyledBadge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    minWidth: theme.spacing(2),
    height: theme.spacing(2),
  },
}));
